import { KEY_STATES } from '../../features/keypads/catalog/keyState';
import type { KeyState } from '../../features/keypads/catalog/keyState';
import { KEYPAD_SCHEMA_VERSION } from '../../features/keypads/catalog/storage';
import { decodeBook } from '../../features/keypads/decode';
import { sortedKeypads } from '../../features/keypads/model';
import type { KeypadBook, KeypadKey } from '../../features/keypads/model';
import { cellInt, cellNumbers, cellString, cellText, listCell, numberCell } from '../../lib/export/cell';
import { EXPORT_FIELD, EXPORT_SECTION } from '../../lib/export/catalog';
import type { CsvRow } from '../../lib/export/csv';
import type { DocumentRow, ExportDocument } from '../../lib/export/document';
import { rowsOf } from '../../lib/export/document';
import type { SectionRows } from '../../lib/export/index';
import { EXPORT_ISSUE, issueFor } from '../../lib/export/issue';
import { fail, ok } from '../../lib/export/result';
import type { ExportResult } from '../../lib/export/result';

/**
 * The keypad book, as rows and back again.
 *
 * This is the second half of the seam `keypadTargets.ts` opens: the feature owns
 * its book and imports nothing from the rest of the app, so the code that knows
 * how a panel is spelled in a spreadsheet lives out here beside it, with the
 * console. It imports the feature's own model and decoder and the generic export
 * codec, and nothing else.
 *
 * A panel, its keys and what each key state drives are three sections, because
 * they are three shapes: one panel, many keys, many mappings. A mapping row is
 * one (key, state, output) triple, so a hand edit adds an output to a state by
 * adding one row and touching nothing else.
 *
 * Reading rebuilds the shape `features/keypads/decode.ts` already accepts and
 * hands it to `decodeBook` — the same guard the tablet's own storage passes
 * through. Panels the decoder refuses are dropped and counted rather than taking
 * the whole file with them.
 */

/** The loads one key drives in one state. */
const loadsOf = (key: KeypadKey, state: KeyState): KeypadKey['states'][number]['loads'] =>
  key.states.find((mapping) => mapping.state === state)?.loads ?? [];

/** True when the cell names a state this build knows. */
const isKeyState = (value: string): value is KeyState =>
  KEY_STATES.some((state) => state === value);

/** The `written` fingerprints, in trigger order so two exports agree. */
const writtenRows = (book: KeypadBook): readonly CsvRow[] =>
  Object.entries(book.written)
    .filter(([, fingerprint]) => fingerprint !== '')
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([trigger, fingerprint]): CsvRow => [EXPORT_SECTION.WRITTEN, trigger, fingerprint]);

/**
 * Every row the keypad book occupies, grouped by section.
 *
 * Grouped rather than flat because that is what `buildExport` takes, and because
 * this half of the file spans four sections: a panel, its keys, what each key
 * state drives, and the fingerprints this tablet recorded.
 */
export const keypadRows = (book: KeypadBook): SectionRows => ({
  [EXPORT_SECTION.KEYPAD]: [
    [EXPORT_SECTION.KEYPAD, ...EXPORT_FIELD[EXPORT_SECTION.KEYPAD]],
    ...sortedKeypads(book).map((keypad): CsvRow => [
      EXPORT_SECTION.KEYPAD,
      keypad.id,
      keypad.type,
      keypad.name,
      numberCell(keypad.address),
      numberCell(keypad.cat6Branch),
      keypad.location,
      listCell(keypad.retiredAddresses),
      keypad.createdAt,
    ]),
  ],

  [EXPORT_SECTION.KEYPAD_KEY]: [
    [EXPORT_SECTION.KEYPAD_KEY, ...EXPORT_FIELD[EXPORT_SECTION.KEYPAD_KEY]],
    ...sortedKeypads(book).flatMap((keypad) =>
      [...keypad.keys]
        .sort((left, right) => left.index - right.index)
        .map((key): CsvRow => [
          EXPORT_SECTION.KEYPAD_KEY,
          keypad.id,
          key.id,
          numberCell(key.index),
          key.name,
        ]),
    ),
  ],

  [EXPORT_SECTION.KEY_MAPPING]: [
    [EXPORT_SECTION.KEY_MAPPING, ...EXPORT_FIELD[EXPORT_SECTION.KEY_MAPPING]],
    ...sortedKeypads(book).flatMap((keypad) =>
      [...keypad.keys]
        .sort((left, right) => left.index - right.index)
        .flatMap((key) =>
          KEY_STATES.flatMap((state) =>
            loadsOf(key, state).map((load): CsvRow => [
              EXPORT_SECTION.KEY_MAPPING,
              keypad.id,
              key.id,
              state,
              load.targetId,
              numberCell(load.value),
            ]),
          ),
        ),
    ),
  ],

  [EXPORT_SECTION.WRITTEN]: [
    [EXPORT_SECTION.WRITTEN, ...EXPORT_FIELD[EXPORT_SECTION.WRITTEN]],
    ...writtenRows(book),
  ],
});

/** The panel rows, with their keys and mappings attached, as the decoder reads them. */
const keypadValues = (document: ExportDocument): readonly unknown[] => {
  const keys = rowsOf(document, EXPORT_SECTION.KEYPAD_KEY);
  const mappings = rowsOf(document, EXPORT_SECTION.KEY_MAPPING);

  return rowsOf(document, EXPORT_SECTION.KEYPAD).map((panel: DocumentRow) => {
    const panelId = cellString(panel, 'id');
    const panelKeys = keys.filter((key) => cellString(key, 'keypadId') === panelId);

    return {
      id: panelId,
      type: cellString(panel, 'type'),
      name: cellString(panel, 'name'),
      address: cellInt(panel, 'address'),
      cat6Branch: cellInt(panel, 'cat6Branch'),
      location: cellString(panel, 'location'),
      retiredAddresses: cellNumbers(panel, 'retiredAddresses'),
      createdAt: cellString(panel, 'createdAt'),
      keys: panelKeys.map((key) => {
        const keyId = cellString(key, 'id');
        const states = KEY_STATES.flatMap((state) => {
          const loads = mappings
            .filter(
              (row) =>
                cellString(row, 'keypadId') === panelId &&
                cellString(row, 'keyId') === keyId &&
                cellString(row, 'state') === state,
            )
            .flatMap((row) => {
              const targetId = cellText(row, 'targetId');
              const value = cellInt(row, 'value');
              return targetId === null || value === null ? [] : [{ targetId, value }];
            });
          return loads.length === 0 ? [] : [{ state, loads }];
        });

        return {
          id: keyId,
          index: cellInt(key, 'index'),
          name: cellString(key, 'name'),
          states,
        };
      }),
    };
  });
};

/** The `written` section as the decoder reads it: trigger to fingerprint. */
const writtenValue = (document: ExportDocument): Readonly<Record<string, string>> =>
  Object.fromEntries(
    rowsOf(document, EXPORT_SECTION.WRITTEN).flatMap((row) => {
      const trigger = cellText(row, 'trigger');
      const fingerprint = cellText(row, 'fingerprint');
      return trigger === null || fingerprint === null ? [] : [[trigger, fingerprint] as const];
    }),
  );

/** What reading a file's keypad half produced. */
export interface DecodedBookRows {
  readonly book: KeypadBook;
  /** Panels the decoder refused, plus mapping rows naming an unknown state. */
  readonly droppedRecords: number;
}

/**
 * Rebuilds the stored book shape from a document, then decodes it.
 *
 * A mapping row naming a state this build does not know is skipped rather than
 * failing the panel: press and release are the only states the firmware's own
 * validator accepts, so an unknown one is a hand-edit mistake, not a reason to
 * refuse the file.
 */
export const keypadBookFrom = (
  document: ExportDocument,
  now: string,
): ExportResult<DecodedBookRows> => {
  const unknownStates = rowsOf(document, EXPORT_SECTION.KEY_MAPPING).filter(
    (row) => !isKeyState(cellString(row, 'state')),
  ).length;

  const decoded = decodeBook({
    schemaVersion: KEYPAD_SCHEMA_VERSION,
    keypads: keypadValues(document),
    written: writtenValue(document),
    updatedAt: document.exportedAt === '' ? now : document.exportedAt,
  });

  if (decoded === null) return fail(issueFor(EXPORT_ISSUE.KEYPADS_UNREADABLE));

  return ok({
    book: decoded.book,
    droppedRecords: decoded.droppedRecords + unknownStates,
  });
};

/** What a book holds, so the console can describe it before applying it. */
export interface BookCounts {
  readonly keypads: number;
  readonly keys: number;
  readonly mappings: number;
  readonly written: number;
}

export const bookCounts = (book: KeypadBook): BookCounts => ({
  keypads: book.keypads.length,
  keys: book.keypads.reduce((total, keypad) => total + keypad.keys.length, 0),
  mappings: book.keypads.reduce(
    (total, keypad) =>
      total +
      keypad.keys.reduce(
        (keyTotal, key) =>
          keyTotal + key.states.reduce((stateTotal, mapping) => stateTotal + mapping.loads.length, 0),
        0,
      ),
    0,
  ),
  written: Object.keys(book.written).length,
});

/**
 * The book type, re-exported so a console panel can name it without reaching
 * past the feature's own public surface for it.
 */
export type { KeypadBook };
